import React from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, ShoppingCart } from 'lucide-react';
interface OrderBumpProps {
  variant: 'sos' | 'super';
  checked: boolean;
  onToggle: () => void;
  title: string;
  description: string;
  features: string[];
  priceLine: string;
  imageUrl: string;
  imageAlt: string;
}
const variantStyles = {
  sos: {
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    accent: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-800',
    checkBg: 'bg-amber-500',
    icon: Zap
  },
  super: {
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    accent: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-800',
    checkBg: 'bg-amber-500',
    icon: ShoppingCart
  }
};
export function OrderBump({
  variant,
  checked,
  onToggle,
  title,
  description,
  features,
  priceLine,
  imageUrl,
  imageAlt
}: OrderBumpProps) {
  const styles = variantStyles[variant];
  const Icon = styles.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4
  }} className={`${styles.bg} border-2 border-dashed ${styles.border} rounded-2xl p-5 md:p-6 text-left relative overflow-hidden`} data-id="element-334">
      {/* Shimmer sweep effect - only when unchecked, draws attention subtly */}
      {!checked && <motion.div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-1/3" style={{
      background: 'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)'
    }} animate={{
      x: ['-100%', '400%']
    }} transition={{
      duration: 2.2,
      repeat: Infinity,
      repeatDelay: 3.5,
      ease: 'easeInOut'
    }} data-id="element-335" />}

      <div className="flex flex-col md:flex-row gap-5 relative z-10" data-id="element-336">
        {/* Image */}
        <div className="flex-shrink-0 mx-auto md:mx-0 w-32 md:w-40" data-id="element-337">
          <img src={imageUrl} alt={imageAlt} className="w-full h-auto object-contain rounded-xl shadow-md" data-id="element-338" />
        </div>

        {/* Content */}
        <div className="flex-1" data-id="element-339">
          {/* Badge */}
          <div className={`inline-flex items-center gap-1.5 ${styles.badgeBg} ${styles.badgeText} px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3`} data-id="element-340">
            <Icon className="w-3.5 h-3.5" data-id="element-341" />
            Oferta única
          </div>

          {/* Title */}
          <h3 className={`text-lg md:text-xl font-bold ${styles.accent} mb-3 leading-tight`} data-id="element-342">
            {title}
          </h3>

          {/* Description */}
          <p className="text-stone-700 text-sm md:text-base mb-4 leading-relaxed" data-id="element-343">
            {description}
          </p>

          {/* Features */}
          <ul className="space-y-2 mb-4" data-id="element-344">
            {features.map((feature, i) => <li key={i} className="flex items-start gap-2 text-sm text-stone-700" data-id="element-345">
                <Check className={`w-4 h-4 ${styles.accent} shrink-0 mt-0.5`} strokeWidth={3} data-id="element-346" />
                <span data-id="element-347">{feature}</span>
              </li>)}
          </ul>

          {/* Checkbox CTA */}
          <button type="button" onClick={onToggle} className={`w-full bg-white border-2 ${checked ? styles.border : 'border-stone-300'} rounded-xl p-4 flex items-center gap-3 text-left transition-all hover:shadow-md cursor-pointer relative z-10`} data-id="element-348">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-all ${checked ? `${styles.checkBg} border-2 ${styles.border}` : 'bg-white border-2 border-stone-400'}`} data-id="element-349">
              {checked && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-350" />}
            </div>
            <span className="text-sm md:text-base font-semibold text-stone-800" data-id="element-351">
              {priceLine}
            </span>
          </button>
        </div>
      </div>
    </motion.div>;
}